/**
 * Long Tasks capture — the web analog of ANR. PerformanceObserver('longtask') sees
 * main-thread blocks; tasks >= longTaskMinMs become perf events kind="longtask",
 * and the collector opens a ui-freeze incident for tasks >= 1s.
 */
import type { ResolvedConfig } from './config.js';
import { uuid } from './util.js';
import { sendPerfEvents } from './perfsend.js';

export function installLongTasks(cfg: ResolvedConfig): () => void {
  if (
    typeof window === 'undefined' ||
    typeof PerformanceObserver === 'undefined' ||
    !cfg.captureLongTasks
  ) {
    return () => {};
  }

  let buf: Record<string, unknown>[] = [];
  const flush = (beacon = false) => {
    const batch = buf;
    buf = [];
    sendPerfEvents(cfg, batch, beacon);
  };

  let obs: PerformanceObserver | null = null;
  try {
    obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration < cfg.longTaskMinMs) continue;
        buf.push({
          id: uuid(),
          project_id: cfg.projectId,
          environment: cfg.environment,
          kind: 'longtask',
          name: 'main-thread-block',
          value: Math.round(e.duration),
          route: location.pathname,
          session_id: cfg.sessionId ?? null,
          timestamp: new Date().toISOString(),
        });
      }
      if (buf.length >= 10) flush();
    });
    obs.observe({ type: 'longtask', buffered: true });
  } catch {
    return () => {}; // longtask entry type unsupported
  }

  const timer = setInterval(() => flush(), 15_000);
  const onHide = () => {
    if (document.visibilityState === 'hidden') flush(true);
  };
  document.addEventListener('visibilitychange', onHide);

  return () => {
    obs?.disconnect();
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onHide);
    flush(true);
  };
}
